<script setup>
import { computed, onMounted, ref, watch } from "vue";
import TagApi from "@api/tag.api";
import FormFileUpload from "@/components/form/FormFileUpload.vue";
import { useMetadataStore } from "@store/metadata";
import GroupFormElSelect from "@/components/form/GroupFormElSelect.vue";
import HandleGroupSelectData from "@/services/HandleGroupSelectData";
import { ElNotification } from "element-plus";
import DocumentCategoryMappingApi from "@api/documentCategoryMapping.api";
import aiFormProcessor from "@services/aiFormProcessor";

const props = defineProps({
    document: {
        type: Object,
    },
    v$: {
        type: Object,
    },
    formErrors: {
        type: Object,
    },
    isUpdateForm: {
        type: Boolean,
    },
    categoryMappings: {
        type: Object,
        default: () => ({})
    },
});

const state = useMetadataStore();
const metadata = computed(() => state.getMetaData);

// Get tags from metadata store
const tagsFromMeta = computed(() => metadata.value.tags || []);

// Convert tags to the same format as usersForSelect
const tagsForSelect = computed(() => {
    return tagsFromMeta.value
        .filter((tag) => tag && tag.name && typeof tag.name === "string")
        .map((tag) => ({
            id: tag.id,
            name: tag.name,
        }));
});

const documentTypesForSelect = computed(() => {
    
    const raw = metadata.value.documentTypes ?? [];
    const allDocumentTypes = HandleGroupSelectData(raw) ?? [];
    
    // Filter document types that are mapped to Category3 (Other Documents - category_id: 3)
    // Use props.categoryMappings instead of making API call
    const category3DocumentTypeIds = props.categoryMappings[1] || [];
    
    // Filter all document types to only include mapped ones
    return allDocumentTypes.filter(docType => 
        category3DocumentTypeIds.includes(docType.id)
    );
});
const usersForSelect = computed(() => metadata.value.users ?? []);

// Use AI form processor service
const isAIProcessing = ref(false);
const isFormReadonly = ref(false);
const aiMode = ref('ai'); // Default to AI mode for documents since file upload is primary

// Initialize form state based on AI mode (only for create forms)
if (!props.document.id && aiMode.value === 'ai') {
    isFormReadonly.value = true; // Disable fields initially in AI mode for create forms only
}

// Inline required validation for files (create mode only)
const filesErrors = computed(() => {
    const errs = Array.isArray(props.formErrors?.files)
        ? [...props.formErrors.files]
        : [];
    const isCreate = !props.document?.id;
    const noFiles =
        !Array.isArray(props.document?.files) ||
        props.document.files.length === 0;
    if (isCreate && noFiles) {
        errs.push("Please upload at least one file.");
    }
    return errs;
});

const onUploadSuccess = async (response) => {
    if (!props.document.files) props.document.files = [];

    // Store full file object (not only ID)
    props.document.files = [{
        id: response.file.id,
        name: response.file.name,
        original_name: response.file.original_name,
        size: response.file.size,
        type: response.file.type,
        uid: response.file.uid || response.file.id,
    }];

    // Trigger AI processing only if AI mode is selected
    if (aiMode.value === 'ai') {
        isAIProcessing.value = true;
        await aiFormProcessor.processAIAnalysis(response.file.id, props.document);
        isAIProcessing.value = false;
        isFormReadonly.value = false;
    }
};

const onUploadDelete = (file) => {
    delete props.document.files;
};

// Watch for AI mode changes
watch(aiMode, (newMode) => {
    if (newMode === 'manual') {
        // Enable form fields in manual mode
        isFormReadonly.value = false;
        isAIProcessing.value = false;
    } else if (newMode === 'ai' && !props.document.id) {
        // Disable form fields in AI mode (only for create forms)
        isFormReadonly.value = true;
        isAIProcessing.value = false;
    }
});

// Simple tag creation function for form-el-select
const createTag = async (tagName) => {
    try {
        // Create the tag in the database (firstOrCreate handles duplicates)
        const response = await TagApi.store({ name: tagName });

        // Add the new tag to the metadata store
        const newTag = {
            id: response.data.data.tag.id,
            name: response.data.data.tag.name,
        };

        // Ensure the newly created tag is selected by default
        if (!Array.isArray(props.document.tags)) {
            props.document.tags = [];
        }

        // If a temporary label was added by the select, replace it with the new ID
        const lastIdx = props.document.tags.lastIndexOf(tagName);
        if (lastIdx !== -1) {
            props.document.tags.splice(lastIdx, 1, newTag.id);
        } else if (!props.document.tags.includes(newTag.id)) {
            props.document.tags.push(newTag.id);
        }

        // Update metadata store with new tag
        if (typeof state.addTag === "function") {
            state.addTag(newTag);
        } else {
            // Fallback: manually add to metadata
            if (!state.metadata.tags) {
                state.metadata.tags = [];
            }
            const existingTag = state.metadata.tags.find(
                (t) => t.id === newTag.id
            );
            if (!existingTag) {
                state.metadata.tags.push(newTag);
            }
        }

        return newTag;
    } catch (error) {

        // Show user-friendly error message
        const errorMessage = error.response?.data?.errors.message;

        let errorTxt = '';
        if (typeof errorMessage === 'string') {
            errorTxt = errorMessage
        } else {

            errorMessage.forEach(element => {
                errorTxt += element + '\n'
            });
        }
        // Use ElNotification for better UX
        ElNotification({
            title: 'Error',
            message: errorTxt,
            type: 'error',
            position: 'bottom-right',
            duration: 5000,
        });

        throw error;
        
    }
};

onMounted(async () => {
    // Ensure metadata is loaded first
    if (!state.loaded) {
        await state.fetchData();
    }

    // Initialize tags array if it doesn't exist
    // Only initialize on first mount, not when component is recreated due to key changes
    if (!props.isUpdateForm && !Array.isArray(props.document.tags)) {
        props.document.tags = [];
    }
});
</script>

<template>
    <div>
        <!-- AI/Manual Mode Selection -->
        <div class="row m-0 my-2" v-if="!document.id">
            <div class="col-md-12 p-0">
                <label class="form-label mb-3">
                    <strong>How would you like to process this document?</strong>
                </label>
                <div class="d-flex gap-3">
                    <el-radio v-model="aiMode" label="manual">
                        <i class="fas fa-keyboard me-2"></i>
                        Manual Entry
                    </el-radio>
                    <el-radio v-model="aiMode" label="ai">
                        <i class="fas fa-robot me-2"></i>
                        AI-Powered (from uploaded file)
                    </el-radio>
                </div>
            </div>
        </div>

        <template v-if="!document.id">
            <div class="row m-0 my-2">
                <div class="col-md-12 p-0">
                    <label class="form-label mb-2">
                        Upload Document
                        <span class="text-danger astric"> *</span></label
                    >
                    <FormFileUpload
                        :allowed-files="'.jpg, .jpeg, .png, .pdf, .doc, .docx, .xls, .xlsx, .csv, .ppt, .pptx, .mp4, .mov, .vsdx'"
                        :errors="filesErrors"
                        :limit="10"
                        :model="document"
                        name="files"
                        :multiple="false"
                        :required="true"
                        clearable
                        label="Upload Files"
                        type="document_attachment"
                        @successUpload="onUploadSuccess"
                        @successDeleteUpload="onUploadDelete"
                        :disabled="isAIProcessing"
                    >
                    </FormFileUpload>

                    <!-- AI Processing Indicator -->
                    <div v-if="isAIProcessing" class="ai-processing-indicator mt-2">
                        <div class="d-flex align-items-center">
                            <div class="spinner-border spinner-border-sm me-2" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <span class="text-primary">AI is analyzing your document...</span>
                        </div>
                    </div>

                    <small
                        v-for="(error, index) in filesErrors || []"
                        :key="index"
                        class="text-danger"
                        style="font-size: 0.8rem; top: 40px"
                    >
                        {{ error }}
                    </small>
                </div>
            </div>
        </template>
        <div class="row my-2">
            <div class="col-md-6">
                <form-input
                    id="name"
                      v-model="document.title"
                    :errors="v$?.title"
                    :label="$t('fields.document_title')"
                    :required="true"
                    placeholder="What is this document about?"
                    :maxlength="120"
                    type="text"
                    :disabled="isFormReadonly"
                />
            </div>
            <div class="col-md-6">
                <group-form-el-select
                    :errors="formErrors"
                    :label="$t('fields.document_type_input')"
                    :model="document"
                    :options="documentTypesForSelect"
                    :required="true"
                    clearable
                    label-field="name"
                    group-by-field="group"
                    name="document_type_id"
                    placeholder="Select whether this is a Case Study, EM Guide, CXO etc"
                />
            </div>
        </div>
        <div class="row my-2">
            <div class="col-md-12 mb-3">
                <form-tiptap-text-area
                    id="name"
                  v-model="document.document_context"
                    :errors="v$?.document_context"
                    :label="$t('fields.document_context')"
                    :required="true"
                    placeholder="When/why this was created?"
                    rows="4"
                    :is-disabled="isFormReadonly"
                />
            </div>
            <div class="col-md-12 mb-3">
                <form-tiptap-text-area
                    id="name"
                    v-model="document.document_purpose"
                    :errors="v$?.document_purpose"
                    :label="$t('fields.document_purpose')"
                    :required="true"
                    placeholder="How will it help TAMers in future?"
                    rows="4"
                    :is-disabled="isFormReadonly"
                />
            </div>

            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.authors')"
                    :model="document"
                    :multiple="true"
                    :options="usersForSelect"
                    :required="true"
                    clearable
                    label-field="fields.team_ids"
                    name="authors_ids"
                    placeholder="Select the TAMers who worked on creating this document."
                    :disabled="isFormReadonly"
                />
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="'Tags'"
                    :model="document"
                    :multiple="true"
                    :options="tagsForSelect"
                    :required="false"
                    :allow-create="true"
                    :on-create-tag="createTag"
                    clearable
                    label-field="name"
                    name="tags"
                    placeholder="AI generated Smart Tags"
                    :disabled="isFormReadonly"
                />
                <small
                    v-for="(error, index) in formErrors?.tags || []"
                    :key="index"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error }}
                </small>
            </div>
        </div>
    </div>
</template>
